import type { ReactNode } from 'react';
import { Box, Flex } from '@/ui';

export type SegmentedOption<T extends string> = {
  label: ReactNode;
  value: T;
  // Accessible name for the button when `label` is a non-textual glyph
  // (e.g. the title-size "A" previews). Falls back to the label otherwise.
  ariaLabel?: string;
};

type SegmentedControlProps<T extends string> = {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * Called with a segment's value while it is hovered, and with null once it is
   * not — for previewing the choice live before it is made. Hover is a
   * mouse-only affordance; touch users tap to apply.
   */
  onPreview?: (value: T | null) => void;
};

export const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  onPreview,
}: SegmentedControlProps<T>) => (
  <Flex bg="rgba(0,0,0,0.05)" borderRadius="ah-lg" gap={0} p={0.5} w="100%">
    {options.map((opt) => {
      const isActive = opt.value === value;
      return (
        <Box
          _hover={isActive ? undefined : { bg: 'rgba(0,0,0,0.04)' }}
          aria-label={opt.ariaLabel}
          as="button"
          bg={isActive ? 'white' : 'transparent'}
          borderRadius="ah-md"
          boxShadow={isActive ? '0 1px 2px rgba(0,0,0,0.08)' : undefined}
          color={isActive ? 'ink.strong' : 'ink.muted'}
          cursor="pointer"
          flex={1}
          fontSize="ah-control"
          fontWeight={600}
          key={opt.value}
          py={1.5}
          textAlign="center"
          transition="background 0.14s, color 0.14s"
          type="button"
          onMouseEnter={() => onPreview?.(opt.value)}
          onMouseLeave={() => onPreview?.(null)}
          onClick={() => {
            // The click commits the previewed value, so release the preview
            // and let the committed one drive the page from here.
            onPreview?.(null);
            onChange(opt.value);
          }}
        >
          {opt.label}
        </Box>
      );
    })}
  </Flex>
);
